import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import {
  exposePdfExportMapReadiness,
  prepareMapForPdfExport
} from "../lib/pdf_export_helpers.mjs"

// Connects to data-controller="provider-detail-map"
// Displays a small map with a single marker for a provider's location
export default class extends Controller {
  static targets = ["map"]
  static values = {
    accessToken: String,
    latitude: Number,
    longitude: Number
  }

  connect() {
    this.mapInitializationFrame = null
    this.mapInitialization = new Promise((resolve) => {
      this.resolveMapInitialization = resolve
    })
    this.removePdfExportMapReadiness = exposePdfExportMapReadiness(
      this.element,
      () => prepareMapForPdfExport(this.mapInitialization)
    )

    if (this.latitudeValue != null && this.longitudeValue != null) {
      this.mapInitializationFrame = requestAnimationFrame(() => {
        this.mapInitializationFrame = null
        this.initializeMap()
      })
    } else {
      this.resolveMapInitialization(null)
    }
  }

  initializeMap() {
    mapboxgl.accessToken = this.accessTokenValue

    try {
      this.map = new mapboxgl.Map({
        container: this.mapTarget,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [this.longitudeValue, this.latitudeValue],
        zoom: 14,
        attributionControl: false,
        interactive: true,
        // Keep the WebGL buffer so the map can be snapshotted into the PDF export.
        preserveDrawingBuffer: true
      })
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-left')

      this.map.on('load', () => {
        const el = document.createElement('div')
        el.className = 'provider-map-marker'

        new mapboxgl.Marker(el)
          .setLngLat([this.longitudeValue, this.latitudeValue])
          .addTo(this.map)
      })
      this.resolveMapInitialization(this.map)
    } catch (error) {
      this.resolveMapInitialization(null)
      console.error("Failed to initialize detail map:", error)
    }
  }

  disconnect() {
    if (this.mapInitializationFrame != null) {
      cancelAnimationFrame(this.mapInitializationFrame)
      this.mapInitializationFrame = null
    }
    this.resolveMapInitialization?.(null)
    this.removePdfExportMapReadiness?.()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }
}
